import { ApiError } from '@/lib/api';

/** Stable permission keys used by Users / Roles UI. */
export function hasPermission(permissions: string[] | undefined, key: string): boolean {
  return Array.isArray(permissions) && permissions.includes(key);
}

export function canViewUsers(permissions: string[] | undefined): boolean {
  return hasPermission(permissions, 'users.view');
}

export function canManageUsers(permissions: string[] | undefined): boolean {
  return hasPermission(permissions, 'users.manage');
}

export function canSuspendUsers(permissions: string[] | undefined): boolean {
  return hasPermission(permissions, 'users.suspend');
}

export function canRemoveUsers(permissions: string[] | undefined): boolean {
  return hasPermission(permissions, 'users.remove');
}

export function canAssignRoles(permissions: string[] | undefined): boolean {
  return hasPermission(permissions, 'users.assign_roles');
}

export function canManageRoles(permissions: string[] | undefined): boolean {
  return hasPermission(permissions, 'roles.manage');
}

export function formatApiError(err: unknown, fallback = 'Request failed'): string {
  if (err instanceof ApiError) {
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

/** Permission dependency map (view capabilities implied by manage/export/etc.). */
const IMPLIES_VIEW: Record<string, string[]> = {
  'users.manage': ['users.view'],
  'users.suspend': ['users.view'],
  'users.remove': ['users.view'],
  'users.assign_roles': ['users.view'],
  'users.grant_admin': ['users.view', 'users.assign_roles'],
  'roles.manage': ['users.view'],
  'sessions.view_identity': ['sessions.view'],
  'sessions.view_transcript': ['sessions.view'],
  'sessions.view_perception': ['sessions.view'],
  'sessions.view_admin_owned': ['sessions.view'],
  'sessions.view_autotest': ['sessions.view'],
  'sessions.observe_live': ['sessions.view'],
  'sessions.control_live': ['sessions.view', 'sessions.observe_live'],
  'sessions.end': ['sessions.view'],
  'sessions.export': ['sessions.view'],
  'sessions.export_report': ['sessions.view'],
  'sessions.delete': ['sessions.view'],
  'agents.manage': ['agents.view'],
  'agents.publish': ['agents.view', 'agents.manage'],
  'agents.delete': ['agents.view'],
  'agents.test_call': ['agents.view'],
  'perception.manage': ['perception.view'],
  'recordings.download': ['recordings.view'],
  'recordings.delete': ['recordings.view'],
  'checkups.manage': ['checkups.view'],
  'incidents.manage': ['incidents.view'],
  'settings.manage': ['settings.view'],
  'providers.manage': ['settings.view'],
  'integrations.manage': ['settings.view'],
  'secrets.manage': ['settings.view'],
  'security.manage': ['settings.view'],
  'audit.export': ['audit.view'],
};

export function expandPermissionSelection(keys: string[]): Set<string> {
  const out = new Set<string>();
  const queue = [...keys];
  while (queue.length) {
    const k = queue.pop();
    if (!k || out.has(k)) continue;
    out.add(k);
    for (const dep of IMPLIES_VIEW[k] || []) {
      if (!out.has(dep)) queue.push(dep);
    }
  }
  return out;
}

export function groupEnabledCount(
  groupPermissions: { key: string }[],
  selected: Set<string>,
): { enabled: number; total: number } {
  const total = groupPermissions.length;
  const enabled = groupPermissions.filter((p) => selected.has(p.key)).length;
  return { enabled, total };
}
